/**
 * JourneyQueueMapLibre.js
 *
 * MapLibre GL JS native passenger position tracker for the QueueMapMonitor.
 * Parallel to JourneyQueue.js (Leaflet) — same public API so the two are
 * interchangeable inside queueRegistryRef.
 *
 * Uses:
 *   maplibregl.Marker  (custom HTML element — same SVG icons as Leaflet version)
 *   maplibregl.Popup   (info bubble on click)
 *   GeoJSON source + fill-extrusion line layer for OSRM routes
 *
 * Coordinate convention:
 *   MapLibre uses [lng, lat] (GeoJSON order)
 *   OSRM query uses {lat},{lng}
 *   sessionData.position uses { lat, lng, heading, speed_kmh }
 *
 * window.maplibregl must exist before instantiating this class.
 */

import { SVG_ICONS, SVG_VIEWBOXES, ALERT_COLORS } from "./JourneyQueueSvgIcons.js";

const OSRM_BASE = "https://router.project-osrm.org/route/v1";

const OSRM_PROFILE = {
  driving:     "driving",
  rideshare:   "driving",
  motorcycle:  "driving",
  golf_cart:   "driving",
  transit:     "driving",
  cycling:     "cycling",
  prm_vehicle: "foot",
  walking:     "foot",
  flying:      null,   // great-circle, no OSRM
};

// Marker dimensions per mode
const MARKER_SIZE = {
  flying:  44,
  driving: 34,
  default: 30,
};

export class JourneyQueueMapLibre {
  /**
   * @param {Object} sessionData  Serialized map session from QueueMapMonitorController
   */
  constructor(sessionData) {
    this.id            = sessionData.id;
    this.scope         = sessionData.scope;
    this.movementMode  = sessionData.movement_mode || "walking";
    this.position      = sessionData.position      || { lat: 0, lng: 0 };
    this.alertColor    = sessionData.alert_color   || "green";
    this.sessionStatus = sessionData.session_status;
    this.passenger     = sessionData.passenger     || {};
    this.offer         = sessionData.offer         || {};
    this.pnrLocator    = sessionData.pnr_locator;

    // MapLibre handles
    this._map            = null;
    this._markerEl       = null;   // The DOM element inside the Marker
    this._marker         = null;   // maplibregl.Marker
    this._popup          = null;   // maplibregl.Popup
    this._routeSourceId  = null;
    this._routeLayerId   = null;
  }

  // ── Icon building ────────────────────────────────────────────────────────────

  /**
   * Build the DOM element that MapLibre's Marker wraps.
   * Matches the visual style of the Leaflet JourneyQueue version.
   */
  buildMarkerElement() {
    const color    = ALERT_COLORS[this.alertColor] || "#10B981";
    const iconPath = SVG_ICONS[this.movementMode]  || SVG_ICONS.driving;
    const viewBox  = SVG_VIEWBOXES[this.movementMode] || "0 0 100 100";
    const size     = MARKER_SIZE[this.movementMode] || MARKER_SIZE.default;
    const heading  = this.position?.heading || 0;

    const wrap = document.createElement("div");
    wrap.className = "jq-marker-wrap";
    wrap.style.cssText = `
      width: ${size}px; height: ${size}px;
      color: ${color};
      cursor: pointer;
      position: relative;
      border-radius: 50%;
      background: rgba(7,9,15,0.82);
      border: 2px solid ${color};
      display: flex; align-items: center; justify-content: center;
      box-shadow: 0 2px 8px rgba(0,0,0,0.6);
    `;

    const pulse = document.createElement("div");
    pulse.className = "jq-pulse-ring";
    pulse.style.color = color;
    wrap.appendChild(pulse);

    const svgWrap = document.createElement("div");
    svgWrap.style.cssText = `
      width: ${Math.round(size * 0.6)}px;
      height: ${Math.round(size * 0.6)}px;
      transform: rotate(${heading}deg);
      transition: transform 0.3s ease;
      display: flex; align-items: center; justify-content: center;
    `;
    svgWrap.innerHTML = `
      <svg viewBox="${viewBox}" xmlns="http://www.w3.org/2000/svg"
           style="width:100%;height:100%;fill:${color};">
        <path d="${iconPath}" />
      </svg>
    `;

    wrap.appendChild(svgWrap);
    this._svgWrap = svgWrap;   // keep ref for rotation updates
    return wrap;
  }

  // ── Map lifecycle ────────────────────────────────────────────────────────────

  /**
   * Add this session's marker (and optional OSRM route) to a MapLibre map.
   * @param {maplibregl.Map} map
   * @param {Object|null}    destination  { lat, lng } for OSRM routing
   */
  async addToMap(map, destination = null) {
    this._map = map;
    const { lat, lng } = this.position;
    if (!lat || !lng) return;

    // Popup
    const maplibregl = window.maplibregl;
    this._popup = new maplibregl.Popup({ offset: 20, closeButton: true, maxWidth: "240px" })
      .setHTML(this._buildPopupHTML());

    // Marker
    this._markerEl = this.buildMarkerElement();
    this._marker   = new maplibregl.Marker({ element: this._markerEl, anchor: "center" })
      .setLngLat([lng, lat])
      .setPopup(this._popup)
      .addTo(map);

    // OSRM route
    if (destination && OSRM_PROFILE[this.movementMode]) {
      this._fetchAndDrawRoute(destination).catch(() => {});
    }
  }

  /**
   * Update position and alert color in place — no re-add needed.
   * Called directly from the ActionCable message handler in Index.jsx.
   * @param {{ lat, lng, heading, speed_kmh }} pos
   * @param {string} alertColor
   */
  updatePosition(pos, alertColor) {
    if (!pos || !this._marker) return;

    const { lat, lng, heading } = pos;
    this._marker.setLngLat([lng, lat]);

    if (this._svgWrap) {
      this._svgWrap.style.transform = `rotate(${heading || 0}deg)`;
    }

    if (alertColor && alertColor !== this.alertColor) {
      this.alertColor = alertColor;
      const color = ALERT_COLORS[alertColor] || "#10B981";
      if (this._markerEl) {
        this._markerEl.style.borderColor = color;
        this._markerEl.style.color       = color;
        const path = this._markerEl.querySelector("path");
        if (path) path.setAttribute("fill", color);
      }
    }

    this.position = pos;
  }

  /**
   * Remove marker, popup, and route layer from the map.
   */
  removeFromMap() {
    if (this._popup)  { this._popup.remove(); this._popup = null; }
    if (this._marker) { this._marker.remove(); this._marker = null; }
    this._removeRoute();
    this._map = null;
  }

  // ── OSRM route ───────────────────────────────────────────────────────────────

  async _fetchAndDrawRoute(destination) {
    const { lat, lng } = this.position;
    const profile = OSRM_PROFILE[this.movementMode] || "driving";
    const url = `${OSRM_BASE}/${profile}/${lng},${lat};${destination.lng},${destination.lat}` +
                `?overview=full&geometries=geojson`;

    let geom;
    try {
      const res = await fetch(url, { signal: AbortSignal.timeout?.(8000) });
      const data = await res.json();
      if (!data.routes?.[0]) return;
      geom = data.routes[0].geometry;   // GeoJSON LineString
    } catch {
      return;
    }

    const map = this._map;
    if (!map) return;

    const color         = ALERT_COLORS[this.alertColor] || "#10B981";
    this._routeSourceId = `jq-route-${this.id}`;
    this._routeLayerId  = `jq-route-layer-${this.id}`;

    const doAdd = () => {
      if (!map || map._removed) return;
      try {
        if (map.getSource(this._routeSourceId)) {
          map.getSource(this._routeSourceId).setData({ type: "Feature", geometry: geom });
        } else {
          map.addSource(this._routeSourceId, {
            type:    "geojson",
            data:    { type: "Feature", geometry: geom },
          });
          map.addLayer({
            id:     this._routeLayerId,
            type:   "line",
            source: this._routeSourceId,
            paint: {
              "line-color":     color,
              "line-width":     3,
              "line-dasharray": [3, 3],
              "line-opacity":   0.75,
            },
          });
        }
      } catch { /* layer already exists or map removed */ }
    };

    if (map.isStyleLoaded()) {
      doAdd();
    } else {
      map.once("style.load", doAdd);
    }
  }

  _removeRoute() {
    const map = this._map;
    if (!map || !this._routeLayerId) return;
    try {
      if (map.getLayer(this._routeLayerId))  map.removeLayer(this._routeLayerId);
      if (map.getSource(this._routeSourceId)) map.removeSource(this._routeSourceId);
    } catch { /* ignore — map may have been removed */ }
    this._routeLayerId  = null;
    this._routeSourceId = null;
  }

  // ── Popup HTML ───────────────────────────────────────────────────────────────

  _buildPopupHTML() {
    const alertLabel = {
      green:  "On Time",
      amber:  "Watch",
      red:    "Alert",
      purple: "GDS PNR",
    }[this.alertColor] || "Active";

    const alertBg = {
      green:  "#064E3B",
      amber:  "#78350F",
      red:    "#7F1D1D",
      purple: "#4C1D95",
    }[this.alertColor] || "#1E2D52";

    const alertFg = ALERT_COLORS[this.alertColor] || "#10B981";

    const mode = this.movementMode?.replace(/_/g, " ") || "unknown";

    return `
      <div style="font-family:system-ui,sans-serif;font-size:12px;min-width:180px;padding:2px 0">
        <div style="font-weight:700;color:#E0E0F0;margin-bottom:4px">
          ${this.passenger?.name || "Passenger"}
        </div>
        ${this.pnrLocator
          ? `<div style="font-family:monospace;font-size:11px;color:#818CF8;margin-bottom:3px">
               ${this.pnrLocator}</div>`
          : ""}
        <div style="display:flex;align-items:center;gap:6px;margin-bottom:4px">
          <span style="background:${alertBg};color:${alertFg};font-size:10px;
                        font-weight:700;padding:1px 6px;border-radius:10px;
                        border:1px solid ${alertFg}40">
            ${alertLabel}
          </span>
          <span style="color:#6B7280;text-transform:capitalize">${mode}</span>
        </div>
        ${this.offer?.name
          ? `<div style="color:#9CA3AF;font-size:11px">${this.offer.name}</div>`
          : ""}
        ${this.position?.speed_kmh != null
          ? `<div style="color:#4B5563;font-size:10px;margin-top:2px">
               ${Math.round(this.position.speed_kmh)} km/h</div>`
          : ""}
      </div>
    `;
  }
}

// ── Great-circle arc helpers (for WorldMapView) ─────────────────────────────────

/**
 * Generate an array of [lng, lat] points approximating a great-circle arc
 * between two geographic points.
 *
 * @param {{ lat, lng }} origin
 * @param {{ lat, lng }} dest
 * @param {number} numPoints  Number of intermediate points (default 60)
 * @returns {[number, number][]}  Array of [lng, lat] GeoJSON coordinate pairs
 */
export function greatCircleArc(origin, dest, numPoints = 60) {
  const toRad = (d) => (d * Math.PI) / 180;
  const toDeg = (r) => (r * 180) / Math.PI;

  const lat1 = toRad(origin.lat), lng1 = toRad(origin.lng);
  const lat2 = toRad(dest.lat),   lng2 = toRad(dest.lng);

  // Haversine great-circle distance
  const d = 2 * Math.asin(
    Math.sqrt(
      Math.sin((lat2 - lat1) / 2) ** 2 +
      Math.cos(lat1) * Math.cos(lat2) * Math.sin((lng2 - lng1) / 2) ** 2
    )
  );

  if (d < 0.0001) return [[origin.lng, origin.lat], [dest.lng, dest.lat]];

  const points = [];
  for (let i = 0; i <= numPoints; i++) {
    const f  = i / numPoints;
    const A  = Math.sin((1 - f) * d) / Math.sin(d);
    const B  = Math.sin(f * d) / Math.sin(d);
    const x  = A * Math.cos(lat1) * Math.cos(lng1) + B * Math.cos(lat2) * Math.cos(lng2);
    const y  = A * Math.cos(lat1) * Math.sin(lng1) + B * Math.cos(lat2) * Math.sin(lng2);
    const z  = A * Math.sin(lat1) + B * Math.sin(lat2);
    const la = toDeg(Math.atan2(z, Math.sqrt(x * x + y * y)));
    const lo = toDeg(Math.atan2(y, x));
    points.push([lo, la]);
  }
  return points;
}

// ── Shared arc-snap constants ────────────────────────────────────────────────
// Exported so both WorldMapView (initial render) AND Index.jsx (ActionCable
// position updates) snap air positions to the correct great-circle arc.
// Keeping them here — alongside greatCircleArc — ensures a single source of
// truth for airports and routes.

export const SNAP_AIRPORTS = {
  JFK: { code: "JFK", lat: 40.6413,  lng: -73.7781  },
  BOS: { code: "BOS", lat: 42.3656,  lng: -71.0096  },
  LAS: { code: "LAS", lat: 36.0840,  lng: -115.1537 },
  ATL: { code: "ATL", lat: 33.6407,  lng: -84.4277  },
  LAX: { code: "LAX", lat: 33.9425,  lng: -118.4081 },
};

export const SNAP_KNOWN_ROUTES = [
  { from: "JFK", to: "LAS" },
  { from: "BOS", to: "LAS" },
  { from: "LAS", to: "JFK" },
  { from: "LAS", to: "BOS" },
  // ATL routes (seeded for Phase 2A demo expansion)
  { from: "ATL", to: "LAS" },
  { from: "ATL", to: "LAX" },
  { from: "LAS", to: "ATL" },
  { from: "LAX", to: "ATL" },
  { from: "LAX", to: "JFK" },
  { from: "JFK", to: "LAX" },
];

/**
 * Haversine distance in km between two { lat, lng } objects.
 * Exported for use in Index.jsx ActionCable handler.
 */
export function haversineKm(a, b) {
  const R   = 6371;
  const toR = Math.PI / 180;
  const dLat = (b.lat - a.lat) * toR;
  const dLng = (b.lng - a.lng) * toR;
  const h    = Math.sin(dLat / 2) ** 2 +
               Math.cos(a.lat * toR) * Math.cos(b.lat * toR) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
}

/**
 * snapAirPositionToArc — shared implementation used by both:
 *   • WorldMapView.jsx  (initial marker placement)
 *   • Index.jsx         (ActionCable position update handler)
 *
 * Full arc search (N=400 points) + heading penalty (200 km per 90° of
 * disagreement) to disambiguate converging arcs near airport endpoints.
 *
 * @param {{ lat, lng, heading?: number }} position  Raw DB position
 * @returns {[number, number] | null}  [lng, lat] GeoJSON arc point, or null
 */
export function snapAirPositionToArc(position) {
  if (!position?.lat || !position?.lng) return null;

  const hdg = (position.heading != null && position.heading >= 0) ? position.heading : null;
  const N   = 400;
  let bestArcPt = null;
  let bestScore = Infinity;

  SNAP_KNOWN_ROUTES.forEach(({ from, to }) => {
    const origin = SNAP_AIRPORTS[from];
    const dest   = SNAP_AIRPORTS[to];
    if (!origin || !dest) return;

    const arc = greatCircleArc(origin, dest, N);

    for (let i = 0; i <= N; i++) {
      const pt   = arc[i];
      const dist = haversineKm({ lat: pt[1], lng: pt[0] }, position);

      let headingPenalty = 0;
      if (hdg != null && i < N) {
        const next  = arc[i + 1];
        const dLng  = (next[0] - pt[0]) * Math.PI / 180;
        const cosNL = Math.cos(next[1] * Math.PI / 180);
        const cosPL = Math.cos(pt[1]  * Math.PI / 180);
        const sinNL = Math.sin(next[1] * Math.PI / 180);
        const sinPL = Math.sin(pt[1]  * Math.PI / 180);
        const y = Math.sin(dLng) * cosNL;
        const x = cosPL * sinNL - sinPL * cosNL * Math.cos(dLng);
        const arcBearing = (Math.atan2(y, x) * 180 / Math.PI + 360) % 360;
        const diff = Math.min(Math.abs(arcBearing - hdg), 360 - Math.abs(arcBearing - hdg));
        headingPenalty = (diff / 90) * 200;
      }

      const score = dist + headingPenalty;
      if (score < bestScore) {
        bestScore = score;
        bestArcPt = pt;
      }
    }
  });

  return bestScore < 1200 ? bestArcPt : null;
}
